import { useSessionTokenGetter } from '@develio/auth/client';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getMappedFieldsSummary, mapSubmissionToWizard } from './mapSubmissionToWizard';
import {
  type OrgMember,
  STATUS_COLOR,
  STATUS_LABEL,
  type Submission,
  type SubmissionStatus,
  formatAge,
} from './submissionUtils';
import type { FormField } from './types';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

interface FormSummary {
  id: string;
  name: string;
  fields: FormField[];
}

function getSlaColor(createdAt: string, status: SubmissionStatus): string {
  if (status === 'converted' || status === 'rejected') return 'text-slate-400';
  const days = (Date.now() - new Date(createdAt).getTime()) / 86400000;
  if (days > 3) return 'text-red-600 font-semibold';
  if (days > 1) return 'text-amber-600 font-medium';
  return 'text-green-600';
}

/**
 * Submission detail view — Sprint 12.
 *
 * Shows all submitted field values with their labels, status controls,
 * assignee selector, and a "Promote to Analysis" panel that maps the
 * submission data to wizard defaultValues via location.state.rerunFrom.
 */
export function SubmissionDetailRoute() {
  const { formId, subId } = useParams<{ formId: string; subId: string }>();
  const getToken = useSessionTokenGetter();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState<Submission | null>(null);
  const [form, setForm] = useState<FormSummary | null>(null);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusUpdating, setStatusUpdating] = useState(false);
  const [assigneeUpdating, setAssigneeUpdating] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);

  useEffect(() => {
    if (!formId || !subId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const [detailRes, membersRes] = await Promise.all([
          fetch(`${API}/api/v1/intake-forms/${formId}/submissions/${subId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API}/api/v1/members`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!detailRes.ok) throw new Error('Failed to load submission');
        const detailJson = await detailRes.json();
        if (!cancelled) {
          setSubmission(detailJson.submission as Submission);
          setForm(detailJson.form as FormSummary);
          if (membersRes.ok) {
            const membersJson = await membersRes.json();
            setMembers((membersJson.members ?? []) as OrgMember[]);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formId, subId, getToken]);

  const handleStatusChange = async (status: SubmissionStatus) => {
    if (!submission || !formId || !subId) return;
    setStatusUpdating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/intake-forms/${formId}/submissions/${subId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) return;
      setSubmission((prev) => (prev ? { ...prev, status } : prev));
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleAssigneeChange = async (assignedTo: string | null) => {
    if (!submission || !formId || !subId) return;
    setAssigneeUpdating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/intake-forms/${formId}/submissions/${subId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignedTo }),
      });
      if (!res.ok) return;
      const member = members.find((m) => m.userId === assignedTo);
      setSubmission((prev) =>
        prev
          ? {
              ...prev,
              assignedTo,
              assigneeName: member?.name ?? null,
              assigneeEmail: member?.email ?? null,
            }
          : prev,
      );
    } finally {
      setAssigneeUpdating(false);
    }
  };

  const handlePromote = async () => {
    if (!submission || !form || !formId || !subId) return;
    setPromoting(true);
    setPromoteError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API}/api/v1/intake-forms/${formId}/submissions/${subId}/promote`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Promote failed');
      const wizardDefaults = mapSubmissionToWizard(form.fields, submission.data);
      navigate('/analyses/new', { state: { rerunFrom: wizardDefaults } });
    } catch {
      setPromoteError('Failed to promote. Please try again.');
      setPromoting(false);
    }
  };

  // ── Loading / error ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-slate-500">Loading…</p>
      </div>
    );
  }

  if (error || !submission || !form) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <p className="text-sm text-red-600">{error ?? 'Submission not found.'}</p>
      </div>
    );
  }

  const age = formatAge(submission.createdAt);
  const slaColor = getSlaColor(submission.createdAt, submission.status);
  const mappedPreview = getMappedFieldsSummary(form.fields, submission.data);
  const isActionable = submission.status !== 'converted' && submission.status !== 'rejected';

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Link to="/forms" className="hover:text-slate-900 hover:underline">
            Forms
          </Link>
          <span>/</span>
          <Link to={`/forms/${formId}/edit`} className="hover:text-slate-900 hover:underline">
            {form.name}
          </Link>
          <span>/</span>
          <Link
            to={`/forms/${formId}/submissions`}
            className="hover:text-slate-900 hover:underline"
          >
            Submissions
          </Link>
          <span>/</span>
          <span className="text-slate-700">Detail</span>
        </div>
        <h2 className="mt-1 text-2xl font-semibold tracking-tight">Submission</h2>
      </div>

      {/* Two-column layout */}
      <div className="flex flex-col gap-6 md:flex-row md:items-start">
        {/* ── Left: submitted data ─────────────────────────────────────────── */}
        <div className="flex-1 space-y-5">
          {/* Submitter info card */}
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Submitter
            </h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div>
                <dt className="text-slate-400">Name</dt>
                <dd className="font-medium text-slate-900">
                  {submission.submitterName ?? <span className="text-slate-400 italic">—</span>}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">Email</dt>
                <dd className="text-slate-700">
                  {submission.submitterEmail ? (
                    <a href={`mailto:${submission.submitterEmail}`} className="hover:underline">
                      {submission.submitterEmail}
                    </a>
                  ) : (
                    <span className="text-slate-400 italic">—</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">Submitted</dt>
                <dd className="text-slate-700">
                  {new Date(submission.createdAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </dd>
              </div>
              {submission.submitterIp && (
                <div>
                  <dt className="text-slate-400">IP</dt>
                  <dd className="font-mono text-xs text-slate-500">{submission.submitterIp}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Field values card */}
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Submitted Data
            </h3>

            {form.fields.length === 0 ? (
              <p className="text-sm text-slate-500">This form has no fields.</p>
            ) : (
              <dl className="space-y-4">
                {form.fields.map((field) => {
                  const rawValue = submission.data[field.id];
                  let displayValue: string;
                  if (rawValue === undefined || rawValue === null || rawValue === '') {
                    displayValue = '—';
                  } else if (Array.isArray(rawValue)) {
                    displayValue = rawValue.join(', ') || '—';
                  } else {
                    displayValue = String(rawValue);
                  }
                  return (
                    <div key={field.id}>
                      <dt className="text-xs font-medium text-slate-500">{field.label}</dt>
                      <dd className="mt-0.5 text-sm text-slate-900">{displayValue}</dd>
                    </div>
                  );
                })}
              </dl>
            )}

            {/* Raw JSON collapsible */}
            <details className="mt-5">
              <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-600">
                Raw submission data
              </summary>
              <pre className="mt-2 overflow-x-auto rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                {JSON.stringify(submission.data, null, 2)}
              </pre>
            </details>
          </div>
        </div>

        {/* ── Right: action panel ──────────────────────────────────────────── */}
        <div className="w-full space-y-4 md:w-72 md:shrink-0">
          {/* SLA card */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Age</p>
            <p className={`text-lg font-semibold ${slaColor}`}>{age.label}</p>
            {age.urgent && isActionable && (
              <p className="mt-0.5 text-xs text-amber-600">Needs attention</p>
            )}
          </div>

          {/* Status card */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Status
            </p>
            <span
              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLOR[submission.status]}`}
            >
              {STATUS_LABEL[submission.status]}
            </span>
            {isActionable && (
              <div className="mt-3 flex gap-2">
                {submission.status !== 'in_review' && (
                  <button
                    type="button"
                    disabled={statusUpdating}
                    onClick={() => handleStatusChange('in_review')}
                    className="flex-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                  >
                    Mark In Review
                  </button>
                )}
                <button
                  type="button"
                  disabled={statusUpdating}
                  onClick={() => handleStatusChange('rejected')}
                  className="flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            )}
          </div>

          {/* Assignee card */}
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Assigned To
            </p>
            <select
              value={submission.assignedTo ?? ''}
              onChange={(e) => handleAssigneeChange(e.target.value || null)}
              disabled={assigneeUpdating}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 focus:outline-none disabled:opacity-50"
            >
              <option value="">Unassigned</option>
              {members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name ?? m.email}
                </option>
              ))}
            </select>
          </div>

          {/* Promote card */}
          {isActionable ? (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Promote to Analysis
              </p>
              <p className="mb-3 text-xs text-slate-500">
                Map this submission's data to the analysis wizard for analyst review.
              </p>

              {mappedPreview.length > 0 && (
                <div className="mb-3 rounded-md border border-slate-100 bg-slate-50 p-2">
                  <p className="mb-1 text-xs font-medium text-slate-500">Will pre-fill:</p>
                  <ul className="space-y-0.5">
                    {mappedPreview.map(({ label, value }) => (
                      <li key={label} className="text-xs text-slate-600">
                        <span className="font-medium">{label}:</span>{' '}
                        <span className="text-slate-500">{String(value)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {mappedPreview.length === 0 && (
                <p className="mb-3 text-xs text-slate-400">
                  No fields matched wizard inputs. Wizard will open with empty defaults.
                </p>
              )}

              {promoteError && <p className="mb-2 text-xs text-red-600">{promoteError}</p>}

              <button
                type="button"
                disabled={promoting}
                onClick={handlePromote}
                className="w-full rounded-md bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50"
              >
                {promoting ? 'Promoting…' : 'Promote to Analysis'}
              </button>
            </div>
          ) : submission.status === 'converted' ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-green-600">
                Promoted
              </p>
              <p className="text-xs text-green-700">This submission was promoted to an analysis.</p>
              {submission.linkedAnalysisId && (
                <Link
                  to={`/analyses/${submission.linkedAnalysisId}`}
                  className="mt-2 block text-xs font-medium text-green-700 hover:underline"
                >
                  View Analysis →
                </Link>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
